import { HttpHeaders } from '@angular/common/http';
import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OwlOptions } from 'ngx-owl-carousel-o';
import { forkJoin, of, Subscription } from 'rxjs';
import { SearchService } from '../../_service/search.service';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { WalletinfoService } from 'src/app/_service/walletinfo.service';
import { MycontentserviceService } from 'src/app/_service/mycontentservice.service';
import { WhiteLabelService } from 'src/app/_service/white-label.service';
import { sampleGenreData } from 'src/app/constants/sample';
import { CategoryService } from 'src/app/_service/category.service';
import { GlobaldataService } from 'src/app/_service/globaldata.service';
import { TVNavigationService, Region } from 'src/app/_service/tv-navigation.service';
import { TVFocusMemoryService } from 'src/app/_service/tv-focus-memory.service';
import { TvKeyboardService } from 'src/app/_service/tv-keyboard.service';
import { DeviceService } from 'src/app/_service/tv-detect.service';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.css']
})
export class SearchComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Snapshot of the component's route — see category-movie for rationale. */
  private componentRoute: string = '';

  searchResult:any[] = [];
  isLoading: boolean = false;
  loading:boolean = false;
  ccPrimeList:any[] = [];
  searchTerm = new FormControl();
  suggestionsList: any[] = [];
  contentHistory: any[] = [];
  isSearchInputFocused: boolean = false;
  notFound:boolean = false;

  // TV Navigation Properties
  @ViewChild('searchInput') searchInput: ElementRef;
  @ViewChildren('genreTab') genreTabs: QueryList<ElementRef>;

  isTV: boolean = false;
  focusedSearchIndex: number = -1;
  focusedGenreIndex: number = -1;
  activeRegion: 'search' | 'genres' | 'results' = 'search';

  private subscriptions: Subscription[] = [];

  formhttpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    })
  };
  genreData = sampleGenreData;

  constructor(
    private searchService: SearchService,
    private router: Router,
    private walletService: WalletinfoService,
    private mycontentService: MycontentserviceService,
    private whitelabelService: WhiteLabelService,
    private route: ActivatedRoute,
    private categ: CategoryService,
    private genreService: GlobaldataService,
    private tvNavService: TVNavigationService,
    private focusMemory: TVFocusMemoryService,
    private tvKeyboardService: TvKeyboardService,
    private deviceService: DeviceService
    ) {
    this.setUpSearch();
    this.showHistory();
  }

  setUpSearch(): void {
    this.loading = true;
    const sub = this.searchTerm.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term: string) => {
        if (term) {
          this.isSearchInputFocused = false;
          return this.searchService.getCombinedSuggestions(term);
        }
        // Emit an empty array (not from([])) so the subscriber fires with []
        // and clears stale suggestions in suggestionsList + the TV keyboard.
        return of([]);
      })
    ).subscribe((suggestions: any[]) => {
      this.suggestionsList = suggestions || [];
      // Always sync to keyboard — including empty arrays, so stale suggestions
      // from a previous search don't linger when the new query has no matches.
      if (this.isTV && this.tvKeyboardService.isVisible()) {
        this.tvKeyboardService.setSuggestions(this.suggestionsList);
      }
      this.loading = false;
    }, error => {
      this.loading = false;
    });
    this.subscriptions.push(sub);
  }

  showHistory(): void {
    this.loading = true;
    this.searchService.getSearchHistory().subscribe(result => {
      this.loading = false;
      console.log(result);
      this.contentHistory = result;
    }, error => {
      this.loading = false;
    });
  }

  onSearch(): void {
    const searchQuery = this.searchTerm.value;
    this.router.navigate(['/tqr/search'], { queryParams: { search_key: searchQuery } });
    this.suggestionsList = [];
    this.contentHistory = [];
  }

  selectSuggestion(suggestion: string): void {
    const route = this.whitelabelService.getPrefixedRoute('search');
    this.closeKeyboard();
    this.router.navigate([route], { queryParams: { search_key: suggestion } });
    this.loadSearchData(suggestion);
  }

  /**
   * Load search data without triggering navigation. Used by callers that
   * have already arrived at the correct URL (e.g. queryParamMap subscription
   * after BACK from a movie card, or after the search() router.navigate).
   * Calling router.navigate here would issue a redundant same-URL navigation
   * that, in this app, strips the queryParams from history.pushState — which
   * breaks BACK-restore because the URL ends up as /search without the key.
   */
  private loadSearchData(suggestion: string): void {
    this.searchTerm.setValue(suggestion);
    this.getSearchInfo(suggestion);
    this.getCCPrimeSearchResult(suggestion);
    this.suggestionsList = [];
    this.contentHistory = [];
  }

  closeKeyboard(): void {
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement) activeElement.blur();
  }

  search(){
    const searchQuery = this.searchTerm.value;
    if (searchQuery) {
      const route = this.whitelabelService.getPrefixedRoute('search');
      this.closeKeyboard();
      this.router.navigate([route], { queryParams: { search_key: searchQuery } });
      this.executeSearch(searchQuery); 
      this.suggestionsList = [];
      this.contentHistory = [];
    }
  }

  executeSearch(searchKey: string): void {
    console.log("Executing search for:", searchKey);
    this.getSearchInfo(searchKey);
    this.getCCPrimeSearchResult(searchKey);
  }

  ngOnInit(): void {
    this.isTV = this.deviceService.isTV();

    this.isGenreLoading = true;
    this.genreService.getGenreData().subscribe({
      next: (res:any) => {
        this.genreData = res.responseDto;
        this.isGenreLoading = false;
        this.selectedGenre = res.responseDto[0].value;
        this.getWebData(this.genreData[0]);
      },
      error: () => {
        console.log('Error loading genres');
        this.isGenreLoading = false;
      }
    });
    this.route.queryParamMap.subscribe((result:any)=>{
      console.log(result);
      // Keep componentRoute snapshot in sync with the current URL (incl. query
      // params). ngOnDestroy uses it as the focus-memory key, and by then
      // this.router.url has already moved to the destination route — so we
      // must capture the source URL while we're still on it.
      this.componentRoute = this.router.url;
      const search_key = result.params['search_key'];
      console.log(search_key);
      if(search_key){
        // Use loadSearchData (not selectSuggestion) — URL is already correct
        // for this route. selectSuggestion would issue a redundant
        // router.navigate that strips queryParams from history.pushState.
        this.loadSearchData(search_key);
      }
    })
  }

  ngAfterViewInit(): void {
    if (this.isTV) {
      this.componentRoute = this.router.url;
      setTimeout(() => {
        this.registerSearchNavigation();
        this.registerGenresRegion();
        this.setupFocusSubscriptions();
        this.restoreTvFocus();
      }, 200);

      // Always listen for genre tab changes (they load async from API).
      // registerGenresRegion() may return early if tabs aren't rendered yet,
      // so this ensures re-registration fires once they appear.
      const genresChangeSub = this.genreTabs.changes.subscribe(() => {
        this.tvNavService.unregisterRegion('search-genres');
        setTimeout(() => this.registerGenresRegion(), 50);
      });
      this.subscriptions.push(genresChangeSub);
    }
  }

  registerSearchNavigation(): void {
    if (!this.searchInput) return;

    const searchRegion: Region = {
      id: 'search-input',
      type: 'row',
      elementRef: this.searchInput,
      focusableItems: [this.searchInput]
    };

    this.tvNavService.registerRegion(searchRegion);
  }

  registerGenresRegion(): void {
    if (!this.genreTabs || this.genreTabs.length === 0) return;

    const genresRegion: Region = {
      id: 'search-genres',
      type: 'row',
      elementRef: this.genreTabs.first,
      focusableItems: this.genreTabs.toArray()
    };

    this.tvNavService.registerRegion(genresRegion);
  }

  setupFocusSubscriptions(): void {
    // Subscribe to focus changes
    const focusSub = this.tvNavService.focus$.subscribe((focusKey) => {
      if (focusKey) {
        const [regionId, indexStr] = focusKey.split(':');
        const index = parseInt(indexStr, 10);
        this.updateFocusState(regionId, index);
      }
    });
    this.subscriptions.push(focusSub);

    // Subscribe to active region changes
    const regionSub = this.tvNavService.activeRegion$.subscribe((regionId) => {
      this.updateActiveRegion(regionId);
    });
    this.subscriptions.push(regionSub);
  }

  updateFocusState(regionId: string, index: number): void {
    if (regionId === 'search-input') {
      this.focusedSearchIndex = index;
      this.focusedGenreIndex = -1;
    } else if (regionId === 'search-genres') {
      this.focusedSearchIndex = -1;
      this.focusedGenreIndex = index;

      // Apply focus class to genre tabs and scroll the focused chip into the
      // horizontal scroll viewport. The .genres container has overflow-x:auto
      // with scroll-behavior:smooth (search.component.css). 'inline:center'
      // centers the focused chip horizontally so the user always sees several
      // chips on either side — better orientation than 'nearest' which would
      // pin the chip to the edge. Without this, D-pad navigation past the
      // visible 10-12 chips silently moves focus off-screen across the
      // remaining ~30.
      this.genreTabs.forEach((tab, i) => {
        if (i === index) {
          tab.nativeElement.classList.add('tv-focus');
          tab.nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        } else {
          tab.nativeElement.classList.remove('tv-focus');
        }
      });
    } else {
      this.focusedSearchIndex = -1;
      this.focusedGenreIndex = -1;

      // Clear genre focus class when leaving genres region
      this.genreTabs?.forEach((tab) => {
        tab.nativeElement.classList.remove('tv-focus');
      });
    }
  }

  updateActiveRegion(regionId: string): void {
    if (regionId === 'search-input') {
      this.activeRegion = 'search';
    } else if (regionId === 'search-genres') {
      this.activeRegion = 'genres';
    } else {
      this.activeRegion = 'results';
    }
  }

  onSearchInputActivate(): void {
    if (!this.isTV) return;

    // Mirror keyboard's live value into searchTerm so the existing
    // valueChanges → debounce → suggestions pipeline (in setUpSearch) drives
    // both the input's display value and the keyboard's suggestions panel.
    // Single source of truth — no duplicate stream needed.
    const valueSub = this.tvKeyboardService.currentValue$.subscribe(value => {
      this.searchTerm.setValue(value, { emitEvent: true });
    });

    const cleanup = () => valueSub.unsubscribe();

    this.tvKeyboardService.show({
      inputType: 'text',
      initialValue: this.searchTerm.value || '',
      maxLength: 100,
      onSubmit: (value: string) => {
        cleanup();
        this.searchTerm.setValue(value);
        this.search();
      },
      onCancel: () => {
        cleanup();
      },
      onSuggestionSelect: (suggestion: string) => {
        cleanup();
        this.selectSuggestion(suggestion);
      }
    });
  }

  ngOnDestroy(): void {
    // Save focus position for TV mode
    if (this.isTV) {
      // Dismiss the on-screen keyboard if it's still open
      if (this.tvKeyboardService.isVisible()) {
        this.tvKeyboardService.cancel();
      }

      const regionId = this.tvNavService.getActiveRegionId();
      const index = this.tvNavService.getFocusedIndex();
      if (regionId) {
        // Use componentRoute snapshot, not router.url: by the time ngOnDestroy
        // fires, Angular has already navigated to the destination URL, so
        // router.url would key the saved focus by the wrong route and
        // restoreTvFocus would never find it on BACK.
        this.focusMemory.saveFocusPosition({
          regionId,
          index,
          timestamp: Date.now(),
          route: this.componentRoute || this.router.url
        });
      }

      this.tvNavService.unregisterRegion('search-input');
      this.tvNavService.unregisterRegion('search-genres');
    }
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  getCCPrimeSearchResult(searchTerm:string){
    this.isLoading = true;
    this.searchService.getCCPrimeSearchData(searchTerm).subscribe((result)=>{
      console.log(result);
      this.isLoading = false;
      if(result.length){
        this.notFound = false;
        this.ccPrimeList = result;
        // this.mapwatchContent(this.ccPrimeList, w);
      }else{
        this.notFound = true;
      }
    },(error)=>{
      this.isLoading = false;
    })
  }

  filterSeacrhData(data) {
    const result = [];
      for(const item of data) {
        for(const searchItem of this.searchResult) {
          if(searchItem.vidLibId === item.vidLibId) {
            result.push(searchItem);
            break;
          }
        }
    }
    return result;
  }

  onBackClick() {
    window.history.back();
  }

  getSearchInfo(search_key: string) {
    this.isLoading = true;
    this.loading = true;
    this.notFound = false;
    const userId = localStorage.getItem('userId');
    forkJoin({
      search: this.searchService.getPrimeSearchData(search_key),
      watchlist: this.mycontentService.getWatchListOfUser(userId),
      subscriptions: this.walletService.getUserSubscriptions(),
    }).subscribe(
      ({ search, watchlist, subscriptions }) => {
        if (search?.length) {
          this.searchResult = search;
          this.searchResult.forEach(item => {
            this.mycontentService.transformData(item);
          });
          this.mapwatchContent(this.searchResult, watchlist);       
          this.mapSubscription(this.searchResult, subscriptions);    
          console.log("searchData", this.searchResult);
        } else {
          this.notFound = true;
        }
        this.isLoading = false;
        this.loading = false;
      },
      (error) => {
        this.isLoading = false;
        this.loading = false;
      }
    );
  }
  // getSearchInfo(search_key:string) {
  //   this.isLoading = true;
  //   console.log("getSearchInfo() called");
  //   this.searchService.getPrimeSearchData(search_key).subscribe(result => {
  //     if(result){
  //       this.notFound = false;
  //       this.searchResult = result;
  //       this.searchResult.map((item)=>{
  //         this.mycontentService.transformData(item);
  //       })
  //       console.log("searchData", this.searchResult);
  //       this.loading= false;
  //       this.mapwatchContent(this.searchResult);
  //       this.mapSubscription(this.searchResult);
  //     }else{
  //       this.notFound = true;
  //     }
  //     this.isLoading = false;
  //   },(error)=>{
  //     this.isLoading = false
  //   });
  // }

  clearSearch(): void {
    this.searchTerm.setValue(''); 
    const route = this.whitelabelService.getPrefixedRoute('search');
    this.router.navigate([route]);
    this.searchResult = [];
    this.ccPrimeList = [];
    this.notFound = false;
    this.isSearchInputFocused = false; 
  }

  applyFilter(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.setValue(value);  
    if (value === '') {
      const route = this.whitelabelService.getPrefixedRoute('search');
      this.router.navigate([route]);
      this.searchResult = [];
      this.ccPrimeList = [];
      this.notFound = false;
      this.isSearchInputFocused = false;
    }
  }
  
  getLink(path: string): string {
    return this.whitelabelService.getPrefixedRoute(path);
  }

  genreResult = [];
  isGenreLoading:boolean = false;
  selectedGenre = '';

  getWebData(genre: any) {
    this.genreResult = [];
    this.selectedGenre = genre.value;
    const userId = localStorage.getItem('userId');
    this.isGenreLoading = true;
    forkJoin({
      movies: this.categ.getWebSearch('genre', genre.value),
      watchlist: this.mycontentService.getWatchListOfUser(userId),
      subscriptions: this.walletService.getUserSubscriptions()
    }).subscribe(
      ({ movies, watchlist, subscriptions }) => {
        if (movies?.data?.length) {
          this.genreResult = movies.data.map((item) => {
            this.transformData(item);
            return item;
          });
          this.mapwatchContent(this.genreResult, watchlist);                 
          this.mapSubscription(this.genreResult, subscriptions);               
        }
  
        console.log(this.genreResult);
        this.isGenreLoading = false;
      },
      (error) => {
        this.isGenreLoading = false;
      }
    );
  }
  // getWebData(genre:any){
  //   this.genreResult = [];
  //   this.selectedGenre = genre.value;
  //   const userId = localStorage.getItem('userId');
  //   this.isGenreLoading = true;
  //   this.categ.getWebSearch('genre', genre.value).subscribe((movies) => {
  //     if (movies.data?.length) {
  //       this.genreResult = movies.data.map(section => {
  //         this.transformData(section);
  //         return section;
  //       });
  //       this.mapwatchContent(this.genreResult);
  //       this.mapSubscription(this.genreResult);
  //     }
  //     console.log(this.genreResult);
  //     this.isGenreLoading = false;
  //   }, (error) => {
  //     this.isGenreLoading = false;
  //   });
  // }

  transformData(data: any) {
    this.mycontentService.transformData(data);
  }

  mapwatchContent(list:any, watchresult) {
    list.forEach(section => {
      section.isInWatchList = false;
        watchresult.forEach(wlitem => {
          if (section.vidLibId == wlitem.vidLibId) {
            section.isInWatchList = true;
          }
        })
    })
    this.isLoading=false
  }

  mapSubscription(list:any, subscriptions) {
    list.forEach(section => {
      section.expiringDate = '';
      subscriptions.forEach(sitem => {
        if(section.channelId == sitem.channelId && sitem.isActive){
          section.isChannelSubscribed = true;
          section.expiringDate = sitem.dateOfExpiry;
        }
        if((section.vidLibId == sitem.vidLibId || section.vidLibId == sitem.vidLibId) && sitem.isActive){
          section.isPPVSubscribed = true;
          section.expiringDate = sitem.dateOfExpiry;
        }else if(section.channelId == sitem.channelId && sitem.isActive && section.sub_value === '0'){
          section.isPPVSubscribed = true;
          section.expiringDate = sitem.dateOfExpiry;
        }
      })
    })
  }

  private restoreTvFocus(): void {
    // Priority: lastContentFocus (sidebar switch) > focus memory (route return) > default
    if (this.tvNavService.hasLastContentFocus()) {
      return; // TVNavigationService handles sidebar-to-content restoration
    }

    const saved = this.focusMemory.restoreFocusPosition(this.router.url);
    if (!saved) return;

    // Result thumbnails register their region asynchronously (movie-thumbnail
    // ngAfterViewInit + 100ms + cardRefs.changes), so calling activateRegion
    // immediately on a saved thumbnail-row position silently no-ops because
    // the region doesn't exist yet — focus then defaults to the search input.
    // Poll for the region to appear and activate when it does.
    //
    // Guard: if the active region is no longer 'search-input' (the auto-default
    // when search-input registers), the user has pressed a D-pad key and moved
    // focus during our retry window. Stop interfering — their intent wins.
    const tryActivate = (attemptsLeft: number): void => {
      const current = this.tvNavService.getActiveRegionId();
      if (current && current !== 'search-input' && current !== saved.regionId) {
        return; // user moved focus, abandon restoration
      }
      this.tvNavService.activateRegion(saved.regionId, saved.index);
      if (this.tvNavService.getActiveRegionId() === saved.regionId) return; // success
      if (attemptsLeft <= 0) return;
      setTimeout(() => tryActivate(attemptsLeft - 1), 150);
    };
    tryActivate(10); // up to ~1.5s of retries
  }
}